import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DiscordChannelSettings from "../../components/DiscordChannelSettings";
import axios from "axios";
import "./Accounts.css";

const EditAccount = () => {
    const { accountId } = useParams();
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [accountSettings, setAccountSettings] = useState(null);
    const [loadingAccount, setLoadingAccount] = useState(true);
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [error, setError] = useState("");
    const [settingsError, setSettingsError] = useState("");
    const [savingAccount, setSavingAccount] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        active: false,
        apiKey: "",
        apiSecret: "",
        isGlobal: false,
        isTestnet: false,
    });

    const [settingsForm, setSettingsForm] = useState({
        fullSizeTradePercentage: "",
        lottoTradePercentage: "",
        sizeMarginOfErrorPercentage: "",
        opraFeedEnabled: false,
        sellsDisabled: false,
        fullSizeSellEnabled: false,
        sameDayExpirationLottoEnabled: false,
        weeklyAllocation: "", // Display only
        balanceTrackingEnabled: false,
        preMarketClosePositionClearEnabled: false,
        profitTriggerPercentage: "",
        lossTriggerPercentage: "",
        balanceTrackingTriggerTimeout: "",
        discountedBuyPercentage: "",
        vixPauseEnabled: false,
        vixPauseThreshold: "",
        vvixPauseThreshold: "",
        vixPauseLiveMode: false,
        vixExitAllEnabled: false,
        vixExitAllThreshold: "",
        vvixExitAllThreshold: "",
        ignorePuts: false,
    });

    // Load account data.
    useEffect(() => {
        axios
            .get(`/account/${accountId}`)
            .then((response) => {
                setAccount(response.data);
                setFormData({
                    name: response.data.name,
                    active: response.data.active,
                    apiKey: response.data.apiKey,
                    apiSecret: response.data.apiSecret,
                    isGlobal: response.data.isGlobal,
                    isTestnet: response.data.isTestnet,
                });
                setLoadingAccount(false);
            })
            .catch((err) => {
                console.error("Failed to fetch account:", err);
                setError("Failed to load account.");
                setLoadingAccount(false);
            });
    }, [accountId]);

    // Load account settings.
    useEffect(() => {
        axios
            .get(`/account/settings/${accountId}`)
            .then((response) => {
                setAccountSettings(response.data);
                setSettingsForm({
                    fullSizeTradePercentage: response.data.fullSizeTradePercentage,
                    lottoTradePercentage: response.data.lottoTradePercentage,
                    sizeMarginOfErrorPercentage: response.data.sizeMarginOfErrorPercentage,
                    opraFeedEnabled: response.data.opraFeedEnabled,
                    sellsDisabled: response.data.sellsDisabled,
                    fullSizeSellEnabled: response.data.fullSizeSellEnabled,
                    sameDayExpirationLottoEnabled: response.data.sameDayExpirationLottoEnabled,
                    weeklyAllocation: response.data.weeklyAllocation,
                    balanceTrackingEnabled: response.data.balanceTrackingEnabled,
                    preMarketClosePositionClearEnabled: response.data.preMarketClosePositionClearEnabled,
                    profitTriggerPercentage: response.data.profitTriggerPercentage ?? "",
                    lossTriggerPercentage: response.data.lossTriggerPercentage ?? "",
                    balanceTrackingTriggerTimeout: response.data.balanceTrackingTriggerTimeout ?? "",
                    discountedBuyPercentage: response.data.discountedBuyPercentage ?? "",
                    vixPauseEnabled: response.data.vixPauseEnabled ?? false,
                    vixPauseThreshold: response.data.vixPauseThreshold ?? "",
                    vvixPauseThreshold: response.data.vvixPauseThreshold ?? "",
                    vixPauseLiveMode: response.data.vixPauseLiveMode ?? false,
                    vixExitAllEnabled: response.data.vixExitAllEnabled ?? false,
                    vixExitAllThreshold: response.data.vixExitAllThreshold ?? "",
                    vvixExitAllThreshold: response.data.vvixExitAllThreshold ?? "",
                    ignorePuts: response.data.ignorePuts ?? false,
                });
                setLoadingSettings(false);
            })
            .catch((err) => {
                console.error("Failed to fetch account settings:", err);
                setSettingsError("Failed to load account settings.");
                setLoadingSettings(false);
            });
    }, [accountId]);

    const handleAccountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettingsForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleAccountSubmit = (e) => {
        e.preventDefault();
        setSavingAccount(true);
        axios
            .post("/account/update", {
                accountId: accountId,
                name: formData.name,
                active: formData.active,
                apiKey: formData.apiKey,
                apiSecret: formData.apiSecret,
                isGlobal: formData.isGlobal,
                isTestnet: formData.isTestnet,
            })
            .then((response) => {
                setSavingAccount(false);
                navigate("/accounts");
            })
            .catch((err) => {
                console.error("Failed to update account:", err);
                setError("Failed to update account.");
                setSavingAccount(false);
            });
    };

    const handleSettingsSubmit = (e) => {
        e.preventDefault();
        setSavingSettings(true);
        axios
            .post("/account/settings/update", {
                accountId: accountId,
                fullSizeTradePercentage: settingsForm.fullSizeTradePercentage,
                lottoTradePercentage: settingsForm.lottoTradePercentage,
                sizeMarginOfErrorPercentage: settingsForm.sizeMarginOfErrorPercentage,
                weeklyAllocation: settingsForm.weeklyAllocation,
                opraFeedEnabled: settingsForm.opraFeedEnabled,
                sellsDisabled: settingsForm.sellsDisabled,
                fullSizeSellEnabled: settingsForm.fullSizeSellEnabled,
                sameDayExpirationLottoEnabled: settingsForm.sameDayExpirationLottoEnabled,
                balanceTrackingEnabled: settingsForm.balanceTrackingEnabled,
                preMarketClosePositionClearEnabled: settingsForm.preMarketClosePositionClearEnabled,
                profitTriggerPercentage: settingsForm.profitTriggerPercentage || null,
                lossTriggerPercentage: settingsForm.lossTriggerPercentage || null,
                balanceTrackingTriggerTimeout: settingsForm.balanceTrackingTriggerTimeout || null,
                discountedBuyPercentage: settingsForm.discountedBuyPercentage || null,
                vixPauseEnabled: settingsForm.vixPauseEnabled,
                vixPauseThreshold: settingsForm.vixPauseThreshold || null,
                vvixPauseThreshold: settingsForm.vvixPauseThreshold || null,
                vixPauseLiveMode: settingsForm.vixPauseLiveMode,
                vixExitAllEnabled: settingsForm.vixExitAllEnabled,
                vixExitAllThreshold: settingsForm.vixExitAllThreshold || null,
                vvixExitAllThreshold: settingsForm.vvixExitAllThreshold || null,
                ignorePuts: settingsForm.ignorePuts,
            })
            .then((response) => {
                setSavingSettings(false);
                // Optionally refresh settings data.
            })
            .catch((err) => {
                console.error("Failed to update account settings:", err);
                setSettingsError("Failed to update account settings.");
                setSavingSettings(false);
            });
    };

    const handleRecalculate = () => {
        // This triggers a manual recalculation of the weekly allocation.
        axios
            .post("/account/settings/recalculate", { accountId: accountId })
            .then((response) => {
                // Update the weekly allocation display.
                setSettingsForm((prev) => ({
                    ...prev,
                    weeklyAllocation: response.data.weeklyAllocation,
                }));
            })
            .catch((err) => {
                console.error("Failed to recalculate weekly allocation:", err);
                alert("Failed to recalculate weekly allocation.");
            });
    };

    if (loadingAccount || loadingSettings) {
        return <div className="accounts-loading">Loading...</div>;
    }

    return (
        <div className="edit-account-container">
            <h1>Edit Account</h1>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleAccountSubmit} className="edit-account-form">
                <div className="form-group">
                    <label htmlFor="name">Account Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleAccountChange}
                        required
                    />
                </div>
                <div className="form-group checkbox-group">
                    <label htmlFor="active">Active</label>
                    <input
                        type="checkbox"
                        id="active"
                        name="active"
                        checked={formData.active}
                        onChange={handleAccountChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="apiKey">API Key</label>
                    <input
                        type="password"
                        id="apiKey"
                        name="apiKey"
                        value={formData.apiKey}
                        onChange={handleAccountChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="apiSecret">API Secret</label>
                    <input
                        type="password"
                        id="apiSecret"
                        name="apiSecret"
                        value={formData.apiSecret}
                        onChange={handleAccountChange}
                        required
                    />
                </div>
                <div className="form-group checkbox-group">
                    <label htmlFor="isGlobal">Global Account</label>
                    <input
                        type="checkbox"
                        id="isGlobal"
                        name="isGlobal"
                        checked={formData.isGlobal}
                        onChange={handleAccountChange}
                    />
                </div>
                <div className="form-group checkbox-group">
                    <label htmlFor="isTestnet">Testnet</label>
                    <input
                        type="checkbox"
                        id="isTestnet"
                        name="isTestnet"
                        checked={formData.isTestnet}
                        onChange={handleAccountChange}
                    />
                </div>
                <button type="submit" className="save-account-button" disabled={savingAccount}>
                    {savingAccount ? "Saving..." : "Save Account Changes"}
                </button>
            </form>

            <hr />

            <div className="account-settings-container">
                <h2>Account Settings</h2>
                {settingsError && <div className="error-message">{settingsError}</div>}
                <form onSubmit={handleSettingsSubmit} className="edit-account-form">
                    <div className="form-group">
                        <label htmlFor="fullSizeTradePercentage">Full-Size Trade %</label>
                        <input
                            type="number"
                            id="fullSizeTradePercentage"
                            name="fullSizeTradePercentage"
                            value={settingsForm.fullSizeTradePercentage}
                            onChange={handleSettingsChange}
                            step="0.01"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="lottoTradePercentage">Lotto Trade %</label>
                        <input
                            type="number"
                            id="lottoTradePercentage"
                            name="lottoTradePercentage"
                            value={settingsForm.lottoTradePercentage}
                            onChange={handleSettingsChange}
                            step="0.01"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="sizeMarginOfErrorPercentage">Size Margin of Error %</label>
                        <input
                            type="number"
                            id="sizeMarginOfErrorPercentage"
                            name="sizeMarginOfErrorPercentage"
                            value={settingsForm.sizeMarginOfErrorPercentage}
                            onChange={handleSettingsChange}
                            step="0.5"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="discountedBuyPercentage">Discounted Buy %</label>
                        <input
                            type="number"
                            id="discountedBuyPercentage"
                            name="discountedBuyPercentage"
                            value={settingsForm.discountedBuyPercentage}
                            onChange={handleSettingsChange}
                            step="1"
                            min="0"
                        />
                    </div>
                    <div className="form-group checkbox-group">
                        <label htmlFor="sellsDisabled">Sells Disabled</label>
                        <input
                            type="checkbox"
                            id="sellsDisabled"
                            name="sellsDisabled"
                            checked={settingsForm.sellsDisabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    <div className="form-group checkbox-group">
                        <label htmlFor="opraFeedEnabled">OPRA Feed</label>
                        <input
                            type="checkbox"
                            id="opraFeedEnabled"
                            name="opraFeedEnabled"
                            checked={settingsForm.opraFeedEnabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    <div className="form-group checkbox-group">
                        <label htmlFor="fullSizeSellEnabled">Full Position Sell</label>
                        <input
                            type="checkbox"
                            id="fullSizeSellEnabled"
                            name="fullSizeSellEnabled"
                            checked={settingsForm.fullSizeSellEnabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    <div className="form-group checkbox-group">
                        <label htmlFor="sameDayExpirationLottoEnabled">Same Day Expiration Lotto</label>
                        <input
                            type="checkbox"
                            id="sameDayExpirationLottoEnabled"
                            name="sameDayExpirationLottoEnabled"
                            checked={settingsForm.sameDayExpirationLottoEnabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>Weekly Allocation</label>
                        <div className="weekly-allocation-container">
                            <input
                                type="text"
                                name="weeklyAllocation"
                                value={settingsForm.weeklyAllocation || ""}
                                readOnly
                            />
                            <button
                                type="button"
                                className="recalculate-button"
                                onClick={handleRecalculate}
                            >
                                Re-calculate
                            </button>
                        </div>
                    </div>
                    <hr />
                    <h3>VIX/VVIX Settings</h3>
                    <div className="form-group checkbox-group">
                        <label htmlFor="vixPauseEnabled">Pause Entries on VIX/VVIX Threshold</label>
                        <input
                            type="checkbox"
                            id="vixPauseEnabled"
                            name="vixPauseEnabled"
                            checked={settingsForm.vixPauseEnabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    {settingsForm.vixPauseEnabled && (
                        <>
                            <div className="form-group">
                                <label htmlFor="vixPauseThreshold">VIX Pause Threshold</label>
                                <input
                                    type="number"
                                    id="vixPauseThreshold"
                                    name="vixPauseThreshold"
                                    value={settingsForm.vixPauseThreshold}
                                    onChange={handleSettingsChange}
                                    step="0.5"
                                    min="0"
                                    placeholder="e.g. 30"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="vvixPauseThreshold">VVIX Pause Threshold</label>
                                <input
                                    type="number"
                                    id="vvixPauseThreshold"
                                    name="vvixPauseThreshold"
                                    value={settingsForm.vvixPauseThreshold}
                                    onChange={handleSettingsChange}
                                    step="0.5"
                                    min="0"
                                    placeholder="e.g. 125"
                                />
                            </div>
                            <div className="form-group checkbox-group">
                                <label htmlFor="vixPauseLiveMode">Live Pause/Unpause</label>
                                <input
                                    type="checkbox"
                                    id="vixPauseLiveMode"
                                    name="vixPauseLiveMode"
                                    checked={settingsForm.vixPauseLiveMode}
                                    onChange={handleSettingsChange}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group checkbox-group">
                        <label htmlFor="vixExitAllEnabled">Exit All Positions on VIX/VVIX Threshold</label>
                        <input
                            type="checkbox"
                            id="vixExitAllEnabled"
                            name="vixExitAllEnabled"
                            checked={settingsForm.vixExitAllEnabled}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    {settingsForm.vixExitAllEnabled && (
                        <>
                            <div className="form-group">
                                <label htmlFor="vixExitAllThreshold">VIX Exit Threshold</label>
                                <input
                                    type="number"
                                    id="vixExitAllThreshold"
                                    name="vixExitAllThreshold"
                                    value={settingsForm.vixExitAllThreshold}
                                    onChange={handleSettingsChange}
                                    step="0.5"
                                    min="0"
                                    placeholder="e.g. 35"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="vvixExitAllThreshold">VVIX Exit Threshold</label>
                                <input
                                    type="number"
                                    id="vvixExitAllThreshold"
                                    name="vvixExitAllThreshold"
                                    value={settingsForm.vvixExitAllThreshold}
                                    onChange={handleSettingsChange}
                                    step="0.5"
                                    min="0"
                                    placeholder="e.g. 130"
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group checkbox-group">
                        <label htmlFor="ignorePuts">Ignore PUT Entries</label>
                        <input
                            type="checkbox"
                            id="ignorePuts"
                            name="ignorePuts"
                            checked={settingsForm.ignorePuts}
                            onChange={handleSettingsChange}
                        />
                    </div>
                    <button type="submit" className="save-account-button" disabled={savingSettings}>
                        {savingSettings ? "Saving..." : "Save Settings Changes"}
                    </button>
                </form>
            </div>

            <DiscordChannelSettings accountId={accountId} />
        </div>
    );
};

export default EditAccount;
